import { createContext, type PropsWithChildren, useContext, useState } from 'react';
import { useMatch } from 'react-router';

import { useAppDispatch } from '../../configuration/setup/hooks';
import { routes } from '../../routes/routes';
import {
    controlTowerApi,
    type OverviewQuery,
    useRequestsQuery,
    useTransportsQuery,
} from '../../services/controlTowerApi';
import { requestRow, transportRows } from './transportRows';

const initialQuery: OverviewQuery = {
    search: '',
    plantIds: [],
    carriers: [],
    sort: 'order_number',
    direction: 'asc',
    offset: 0,
    limit: 50,
};
const useTransportState = () => {
    const dispatch = useAppDispatch();
    const conceptActive = Boolean(useMatch(routes.GRAPH_CONCEPT));
    const [query, setQuery] = useState(initialQuery);
    const [transportOffset, setTransportOffset] = useState(0);
    const options = {
        skip: conceptActive,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    };
    const requests = useRequestsQuery(query, { ...options, pollingInterval: query.offset === 0 ? 5000 : 0 });
    const transports = useTransportsQuery(
        { ...query, offset: transportOffset },
        { ...options, pollingInterval: transportOffset === 0 ? 5000 : 0 }
    );
    const requestData = requests.currentData;
    const transportData = transports.currentData;
    const visibleRequests = requestData ?? (requests.isFetching ? requests.data : undefined);
    const visibleTransports = transportData ?? (transports.isFetching ? transports.data : undefined);
    const updateQuery = (patch: Partial<OverviewQuery>) => {
        setQuery(current => ({ ...current, ...patch, offset: 0 }));
        setTransportOffset(0);
    };
    const plants = [...(requests.data?.facets.plants ?? []), ...(transports.data?.facets.plants ?? [])];
    const facets = {
        plants: [...new Map(plants.map(option => [option.id, option])).values()].sort((a, b) =>
            a.label.localeCompare(b.label)
        ),
        carriers: transports.data?.facets.carriers ?? [],
    };
    return {
        refreshAfterSignal: () => {
            updateQuery({});
            dispatch(controlTowerApi.util.invalidateTags(['TransportData']));
        },
        query,
        updateQuery,
        rows: [
            ...(visibleRequests?.items.map(requestRow) ?? []),
            ...(visibleTransports?.items.flatMap(transportRows) ?? []),
        ],
        hasData: (requestData?.unfiltered_total ?? 0) + (transportData?.unfiltered_total ?? 0) > 0,
        facets,
        loading:
            (requests.isFetching && (!requestData || query.offset > 0)) ||
            (transports.isFetching && (!transportData || transportOffset > 0)),
        fetching: requests.isFetching || transports.isFetching,
        error: requests.isError || transports.isError,
        hasMoreRequests: (requestData?.items.length ?? 0) < (requestData?.total ?? 0),
        hasMoreTransports: (transportData?.items.length ?? 0) < (transportData?.total ?? 0),
        loadMoreRequests: () =>
            setQuery(current => ({ ...current, offset: (requestData?.offset ?? 0) + (requestData?.limit ?? 50) })),
        loadMoreTransports: () => setTransportOffset((transportData?.offset ?? 0) + (transportData?.limit ?? 50)),
        reset: () => {
            setQuery(initialQuery);
            setTransportOffset(0);
        },
        retry: () => {
            if (query.offset !== 0 || transportOffset !== 0) {
                updateQuery({});
            } else {
                requests.refetch();
                transports.refetch();
            }
        },
    };
};
const TransportContext = createContext<ReturnType<typeof useTransportState> | null>(null);
export const TransportProvider = ({ children }: PropsWithChildren) => {
    const state = useTransportState();
    return <TransportContext.Provider value={state}>{children}</TransportContext.Provider>;
};
export const useTransportContext = () => {
    const context = useContext(TransportContext);
    if (!context) {
        throw new Error('TransportProvider is missing');
    }
    return context;
};
